import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import getConfig from 'next/config'

import {
  CreatorThemeWrapper,
  useImportProfileMutation,
} from '@web-apps/feature-creator-page'
import {
  Button,
  ButtonVariant,
  Flex,
  Icon,
  Theme,
  Typography,
} from '@web-apps/ui-shared'
import { CreatorPageType } from '@web-apps/utils-types'
import { creatorUrlBuilders } from '@web-apps/utils-shared'

import { CreatorPageContainer } from '../../../CreatorPage'
import {
  serializeData,
  updateCreatorPageDataForPreview,
} from '../../GetStartedPage.helpers'
import { NormalizedFetchedData } from '../../types'

import { getThemes } from './GetStartedPreview.themes'
import {
  StyledActionsContainer,
  StyledGenerateThemeButton,
  StyledPreviousThemeButton,
} from './GetStartedPreview.styles'

type Props = {
  fetchedData: NormalizedFetchedData
  registrationLink: string
  handleAnalytics: (d: string) => void
}

export const GetStartedPreview = ({
  fetchedData,
  registrationLink,
  handleAnalytics,
}: Props) => {
  const { t } = useTranslation(['pages', 'common'])
  const { publicRuntimeConfig } = getConfig()
  const router = useRouter()
  const [currentTheme, setThemeIndex] = useState<number>(-1)
  const [preview, setPreview] = useState<CreatorPageType>()
  const [isPreviousActive, setPreviousActive] = useState(false)
  const [showAnimation, setAnimationFlag] = useState(false)

  const [importProfileData, { isSuccess, isError, data: importedProfile }] =
    useImportProfileMutation()

  const themes = useMemo(() => {
    return getThemes(fetchedData.colors)
  }, [fetchedData])

  const handleAnimation = () => {
    if (!showAnimation) {
      setAnimationFlag(true)
      setTimeout(() => setAnimationFlag(false), 700)
    }
  }

  const nextTheme = () => {
    handleAnimation()
    handleAnalytics(`generate_button_click`)
    if (!isPreviousActive) {
      setPreviousActive(true)
    }
    setThemeIndex((prev) => (prev === themes.length - 1 ? -1 : prev + 1))
  }

  const previousTheme = () => {
    handleAnalytics(`previous_theme_button_click`)
    setThemeIndex((prev) => (prev === -1 ? themes.length - 1 : prev - 1))
  }

  const handleRegister = async () => {
    if (!fetchedData?.creatorPage) return
    const themeName = themes[currentTheme]?.themeName || 'basic'
    handleAnalytics(`register_with_theme_${themeName}`)

    const serializedProfileData = serializeData(
      fetchedData.creatorPage,
      themes[currentTheme]
    )

    await importProfileData(serializedProfileData.profile)
  }

  useEffect(() => {
    if (isSuccess && importedProfile) {
      const completeRegisterLink = creatorUrlBuilders.addParametersToUrl({
        originalUrl: registrationLink,
        params: {
          profile: importedProfile.split('creator-page-imports/')[1],
        },
      })

      router.push(completeRegisterLink)
    }
  }, [
    router,
    registrationLink,
    isSuccess,
    importedProfile,
    publicRuntimeConfig.cookieDomain,
  ])

  useEffect(() => {
    if (fetchedData) {
      setPreview(
        updateCreatorPageDataForPreview(
          fetchedData.creatorPage,
          fetchedData.colors,
          themes[currentTheme]
        )
      )
    }
  }, [fetchedData, currentTheme, themes])

  if (!preview) {
    return null
  }

  return (
    <CreatorThemeWrapper customizationProps={preview.styles}>
      <CreatorPageContainer
        creatorPage={preview}
        preventContactListSubmission
        style={{ paddingBottom: '188px' }}
      />
      <StyledActionsContainer>
        <Flex justify="space-between">
          <StyledPreviousThemeButton
            inverse
            variant={ButtonVariant.PLAIN}
            clickHandler={previousTheme}
            disabled={!isPreviousActive}
          >
            <Icon.Undo />
          </StyledPreviousThemeButton>
          <StyledGenerateThemeButton
            inverse
            clickHandler={nextTheme}
            $showAnimation={showAnimation}
          >
            <Flex align="center" justify="center" gap={8}>
              <Icon.Sparkles width={16} height={16} />
              <span>{t('pages:get_started.generate_new_theme')}</span>
            </Flex>
          </StyledGenerateThemeButton>
        </Flex>
        <Button variant={ButtonVariant.PRIMARY} clickHandler={handleRegister}>
          <Flex align="center" justify="center" gap={8}>
            <span>{t('pages:get_started.register_for_free')}</span>
            <Icon.ArrowRight
              width={16}
              height={16}
              fillColor={Theme.Colors.typography.inverse}
            />
          </Flex>
        </Button>
        {isError && (
          <Typography color="danger">{t('pages:import_error')}</Typography>
        )}
      </StyledActionsContainer>
    </CreatorThemeWrapper>
  )
}
